import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { EspdResponseDto } from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";

const DEBOUNCE_MS = 300;

export const useDebouncedEspdSave = (save: (next: EspdResponseDto) => Promise<unknown>) =>
  useDebouncedSave(save);

export const useDebouncedSave = <T>(save: (next: T) => Promise<unknown>) => {
  const displayer = useApiError();
  const [isSaving, setIsSaving] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  const latest = useRef({ save, displayer });
  const mounted = useRef(true);
  const failed = useRef(false);
  const unsaved = useRef<{ value: T } | undefined>(undefined);
  const inFlight = useRef<Promise<unknown>>(Promise.resolve());

  useEffect(() => {
    latest.current = { save, displayer };
  });
  // StrictMode mounts, unmounts and remounts, so the flag has to be raised again on every mount.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const enqueue = useCallback((next: T) => {
    const attempt = { value: next };
    unsaved.current = attempt;
    setIsSaving(true);
    inFlight.current = inFlight.current
      .catch(() => undefined)
      .then(async () => {
        try {
          await latest.current.save(next);
          failed.current = false;
          if (unsaved.current === attempt) unsaved.current = undefined;
        } catch (e) {
          failed.current = true;
          latest.current.displayer.trigger(e);
        } finally {
          if (mounted.current) {
            setHasFailed(failed.current);
            setIsSaving(false);
          }
        }
      });
    return inFlight.current;
  }, []);

  const debounced = useMemo(() => debounce(enqueue, DEBOUNCE_MS), [enqueue]);

  useEffect(() => () => void debounced.flush(), [debounced]);

  const settle = async () => {
    debounced.flush();
    await inFlight.current.catch(() => undefined);
    const outstanding = unsaved.current;
    if (failed.current && outstanding) {
      await enqueue(outstanding.value).catch(() => undefined);
    }
    return !failed.current;
  };

  return { save: debounced, isSaving, hasFailed, settle };
};
